import * as React from "react";
import type {
    Template,
    TemplateDashboard,
    TemplateDataSettingsUsers,
    TemplateItemInterface,
    TemplateView,
    TemplateViewDB,
} from "src/components/template/types";
import { useLocation, useParams } from "react-router-dom";
import request from "src/utils/request";
import cloneDeep from "lodash-es/cloneDeep";
import isEqual from "lodash-es/isEqual";
import merge from "lodash-es/merge";
import { useAuth } from "src/components/providers/auth-context";
import { useTemplateViews } from "src/components/providers/template-views-provider";
import transformTemplateView from "src/components/template/transform-template-view";

export enum EditModes {
    removeItem = "remove-item",
    editItem = "edit-item",
    addItem = "add-item",
    addChartRow = "add-chart-row",
    addNumbersRow = "add-numbers-row",
    removeRow = "remove-row",
    removeTemplate = "remove-template",
    addTemplate = "add-template",
    editTemplate = "edit-template",
    editDashboard = "edit-dashboard",
    removeDashboard = "remove-dashboard",
    addDashboard = "add-dashboard",
    cloneDashboard = "clone-dashboard",
}

interface RemoveItemOptions {
    dashboardIndex?: undefined;
    templateIndex: number;
    rowIndex: number;
    itemIndex: number;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.removeItem;
}

interface EditItemOptions {
    dashboardIndex?: undefined;
    templateIndex: number;
    rowIndex: number;
    itemIndex: number;
    item: TemplateItemInterface;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.addItem | EditModes.editItem;
}

interface AddTemplateOptions {
    dashboardIndex?: undefined;
    templateIndex: number;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.addTemplate;
}

interface EditTemplateOptions {
    dashboardIndex?: undefined;
    templateIndex: number;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate: Partial<Template>;
    newDashboard?: undefined;
    editMode: EditModes.editTemplate;
}

interface RemoveTemplateOptions {
    dashboardIndex?: undefined;
    templateIndex: number;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.removeTemplate;
}

interface EditDashboardOptions {
    dashboardIndex: number;
    templateIndex?: undefined;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard: Partial<TemplateDashboard>;
    editMode: EditModes.editDashboard;
}

interface RemoveDashboardOptions {
    dashboardIndex: number;
    templateIndex?: undefined;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.removeDashboard;
}

interface AddDashboardOptions {
    dashboardIndex: number;
    templateIndex?: undefined;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.addDashboard;
}

interface CloneDashboardOptions {
    dashboardIndex: number;
    templateIndex?: undefined;
    rowIndex?: undefined;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.cloneDashboard;
}

interface EditRowOptions {
    dashboardIndex?: undefined;
    templateIndex: number;
    rowIndex: number;
    itemIndex?: undefined;
    item?: undefined;
    newTemplate?: undefined;
    newDashboard?: undefined;
    editMode: EditModes.addNumbersRow | EditModes.addChartRow | EditModes.removeRow;
}

type EditOptions =
    | RemoveItemOptions
    | EditItemOptions
    | AddTemplateOptions
    | EditTemplateOptions
    | RemoveTemplateOptions
    | EditDashboardOptions
    | RemoveDashboardOptions
    | AddDashboardOptions
    | CloneDashboardOptions
    | EditRowOptions;

export const TemplateViewEditContext = React.createContext<{
    view?: TemplateView;
    isDirty: boolean;
    discardChanges: () => void;
    saveChanges: (usersInput: TemplateDataSettingsUsers) => void;
    dashboard?: TemplateDashboard;
    editView?: (editOptions: EditOptions) => void;
    availableItems: { [id: string]: TemplateItemInterface };
    onEditMode: boolean;
    setOnEditMode: React.Dispatch<boolean>;
}>({
    availableItems: {},
    isDirty: false,
    saveChanges: (_usersInput) => {},
    discardChanges: () => {},
    onEditMode: false,
    setOnEditMode: (_value) => {},
});

export const TemplateViewEditProvider = ({
    viewType,
    children,
}: {
    viewType: "personalViews" | "teamViews" | "repoViews" | "exec" | "exec-ts" | undefined;
    children: React.ReactNode;
}) => {
    const { dashboardId, teamId } = useParams();
    const { templateViews } = useTemplateViews();
    const { pathname } = useLocation();
    const pathnameSplit = pathname.split("/");
    const path = pathnameSplit && pathnameSplit[pathnameSplit.length - 1];

    const [onEditMode, setOnEditMode] = React.useState<boolean>(false);
    const [internalView, setInternalView] = React.useState<TemplateView | undefined>(undefined);
    const [cleanView, setCleanView] = React.useState<TemplateView | undefined>(undefined);
    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const [availableItems, setAvailableItems] = React.useState<{
        [id: string]: TemplateItemInterface;
    }>({});

    const { isDemo } = useAuth();

    const getView = async () => {
        let defaultView;
        let defaultViewId;
        let selectedTeam;
        if (templateViews && viewType === "personalViews") {
            const { personalViews } = templateViews;
            if (personalViews) {
                defaultView = personalViews.find((v) => v.isDefault === true);
                defaultViewId = defaultView.id;
            }
        } else if (templateViews && viewType === "repoViews") {
            const { repoViews } = templateViews;
            if (repoViews) {
                defaultView = repoViews.find((v) => v.isDefault === true && v.isRepoView === true);
                defaultViewId = defaultView?.id;
            }
        } else if (templateViews && viewType === "teamViews") {
            const { teamViews } = templateViews;
            if (teamViews) {
                selectedTeam = teamViews.find((v) => v.teamId === teamId);
            }
            if (selectedTeam) {
                defaultView = selectedTeam.views.find((v) => v.isDefault === true);
                defaultViewId = defaultView?.id;
            }
        } else if (templateViews && viewType === "exec") {
            const { orgViews } = templateViews;
            if (orgViews) {
                defaultView = orgViews.find((v) => v.isDefault === true);
                defaultViewId = defaultView?.id;
            }
        } else if (templateViews && viewType === "exec-ts") {
            const { orgTimeseriesViews } = templateViews;
            if (orgTimeseriesViews) {
                defaultView = orgTimeseriesViews.find((v) => v.isDefault === true);
                defaultViewId = defaultView?.id;
            }
        }
        const requestUrl = teamId
            ? `/api/template/view?id=${defaultViewId}&teamId=${teamId}`
            : `/api/template/view?id=${defaultViewId}`;

        if (isDemo) {
            // @ts-expect-error expected
            const resp = (await import("src/components/providers/mock-data/template-view"))
                .default as TemplateView;
            setInternalView(transformTemplateView(resp));
            setCleanView(cloneDeep(transformTemplateView(resp)));
        }
        if (defaultViewId && !isDemo)
            try {
                const resp = await request<TemplateView>(requestUrl, {
                    method: "GET",
                });
                setInternalView(transformTemplateView(resp));
                setCleanView(cloneDeep(transformTemplateView(resp)));
            } catch (error) {
                console.error("Failed to fetch view:", error);
            }
    };

    React.useEffect(() => {
        getView();
    }, [templateViews, viewType]);

    React.useEffect(() => {
        if (isEqual(internalView, cleanView)) {
            setIsDirty(false);
        } else {
            setIsDirty(true);
        }
    }, [internalView]);

    const getItems = async () => {
        let response;
        if (isDemo) {
            // @ts-expect-error: Ignore TS1323 for dynamic import
            response = (await import("src/scenes/dashboard/mock-data/template-items-mock-data"))
                .default;
        } else {
            response = await request<{ [id: string]: TemplateItemInterface }>(
                `/api/template/items`
            );
        }
        setAvailableItems(response);
    };

    React.useEffect(() => {
        getItems();
    }, []);

    const getDashboard = () => {
        if (
            !internalView ||
            (!dashboardId && path !== "repo-view" && !pathname.includes("overview"))
        )
            return undefined;
        const urlHandleFound = internalView.dashboards.find((d) => d.urlHandle === dashboardId);
        if (urlHandleFound) return urlHandleFound;
        const urlHandleFoundRepo = internalView.dashboards.find((d) => d.urlHandle === path);
        if (urlHandleFoundRepo) return urlHandleFoundRepo;
        const idFound = internalView.dashboards.find((d) => !d.urlHandle && d.id === dashboardId);
        if (idFound) return idFound;
        if (pathname.includes("overview")) return internalView.dashboards[0];
        return undefined;
    };

    const editView = ({
        dashboardIndex,
        templateIndex,
        rowIndex,
        itemIndex,
        item,
        newDashboard,
        newTemplate,
        editMode,
    }: EditOptions) => {
        setInternalView((prevState) => {
            if (!prevState?.dashboards) return prevState;
            const dashboards = prevState?.dashboards;
            const dashboard = getDashboard();
            if (!dashboards || !dashboard) return prevState;
            if (editMode === EditModes.editDashboard) {
                dashboards[dashboardIndex] = { ...dashboards[dashboardIndex], ...newDashboard };
                return {
                    ...prevState,
                };
            }
            if (editMode === EditModes.removeDashboard) {
                dashboards.splice(dashboardIndex, 1);
                return {
                    ...prevState,
                };
            }
            if (editMode === EditModes.addDashboard) {
                // eslint-disable-next-line no-param-reassign
                prevState.dashboards = [
                    ...dashboards.slice(0, dashboardIndex),
                    {
                        id: `temp-${Date.now().valueOf()}`,
                        title: "Dashboard",
                        urlHandle: "dashboard",
                        defaultDateRange: "week",
                        templates: [],
                    },
                    ...dashboards.slice(dashboardIndex),
                ];
                return {
                    ...prevState,
                };
            }
            if (editMode === EditModes.cloneDashboard) {
                const dashboardToClone = dashboards[dashboardIndex];
                if (!dashboardToClone) return prevState;

                const clonedDashboard = {
                    ...cloneDeep(dashboardToClone),
                    id: `temp-${Date.now().valueOf()}`,
                    title: `${dashboardToClone.title || "Dashboard"} (Clone)`,
                    urlHandle: `${dashboardToClone.urlHandle || "dashboard"}-clone`,
                    templates: dashboardToClone.templates.map((template) => ({
                        ...template,
                        createdById: "defaultUserId",
                    })),
                };

                return {
                    ...prevState,
                    dashboards: [
                        ...dashboards.slice(0, dashboardIndex + 1),
                        clonedDashboard,
                        ...dashboards.slice(dashboardIndex + 1),
                    ],
                };
            }
            if (!dashboard?.templates) return prevState;
            if (editMode === EditModes.addTemplate) {
                dashboard.templates = [
                    ...dashboard.templates.slice(0, templateIndex),
                    {
                        id: `temp-${Date.now().valueOf()}`,
                        title: { value: "Section Title" },
                        dependencies: [],
                        rows: [],
                    },
                    ...dashboard.templates.slice(templateIndex),
                ];
                return { ...prevState };
            }
            const template = dashboard.templates[templateIndex];
            if (!template) return prevState;
            if (editMode === EditModes.addChartRow) {
                template.rows = [
                    ...template.rows.slice(0, rowIndex),
                    { items: [] },
                    ...template.rows.slice(rowIndex),
                ];
            } else if (editMode === EditModes.addNumbersRow) {
                template.rows = [
                    ...template.rows.slice(0, rowIndex),
                    { singleCard: true, items: [] },
                    ...template.rows.slice(rowIndex),
                ];
            } else if (editMode === EditModes.removeRow && rowIndex !== undefined) {
                template.rows.splice(rowIndex, 1);
            } else if (editMode === EditModes.removeTemplate) {
                dashboard.templates.splice(templateIndex, 1);
            } else if (editMode === EditModes.editTemplate) {
                dashboard.templates[templateIndex] = { ...template, ...newTemplate };
            } else if (rowIndex !== undefined) {
                const row = template.rows[rowIndex];
                if (!row) return prevState;

                if (editMode === EditModes.addItem) {
                    row.items = [
                        ...row.items.slice(0, itemIndex),
                        item,
                        ...row.items.slice(itemIndex),
                    ];
                } else if (itemIndex !== undefined) {
                    row.items.splice(itemIndex, 1);
                    if (item) {
                        row.items.splice(itemIndex, 0, item);
                    }
                }
            }
            return {
                ...prevState,
                // dashboards: {
                //     ...prevState.dashboards,
                //     [dashboardId as string]: {
                //         ...dashboard,
                //     },
                // },
            };
        });
    };
    const saveChanges = async (usersInput: TemplateDataSettingsUsers) => {
        const savedTemplate = await request<TemplateViewDB>(`/api/template/view`, {
            method: "POST",
            body: JSON.stringify({
                users: usersInput.users,
                teamId: usersInput.teamId,
                defaultView: true,
                view: internalView,
            }),
        });
        const newView = merge(cloneDeep(internalView), cloneDeep(savedTemplate));
        setInternalView(cloneDeep(newView));
        setCleanView(cloneDeep(newView));
        setIsDirty(false);
    };

    const discardChanges = () => {
        setInternalView(cloneDeep(cleanView));
        setIsDirty(false);
    };

    const value = React.useMemo(
        () => ({
            isDirty,
            saveChanges,
            discardChanges,
            view: internalView,
            availableItems,
            dashboard: getDashboard(),
            editView,
            onEditMode,
            setOnEditMode,
        }),
        [internalView, dashboardId, availableItems, isDirty, onEditMode]
    );

    return (
        <TemplateViewEditContext.Provider value={value}>
            {children}
        </TemplateViewEditContext.Provider>
    );
};

export const useTemplateViewEdit = () => {
    const context = React.useContext(TemplateViewEditContext);
    if (context === undefined) {
        throw new Error(`useTemplateViewEdit must be used within a TemplateViewEditContext`);
    }
    return context;
};
